import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
    addFilterSuccess, deleteFilterSuccess, displayOutputByFilterSuccess, getFiltersSuccess, getFilterSuccess, setFilterError,
    updateFilterSuccess
} from './filterSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { actionTypes, IFilter, ScopeOptions } from './filterTypes';
import { addFilterApi, deleteFilterApi, getFilterApi, getFiltersApi, updateFilterApi } from './filterApi';
import { TSubscribedMsiVessel } from '../../user-management/subscription/subscriptionTypes';
import { RootState } from '../../core/store';
import { FilterWrapper } from './types/classes/FilterWrapper';
import { selectSelectedSubscriptionId, selectSubscribedMsiVessels, selectTeamId, selectUserId } from '../../user-management/appSelectors';
import { IFilterFormData, ISelectedVesselsFormData, IVesselFormData } from '../../shared/sharedTypes';
import { EOutputType, ESelectionType } from '../../shared/sharedEnums';
import { setSelectedVesselsForOutputRequest } from '../search/searchSlice';
import ModalManager from '../../shared/components/modal/ModalManager';
import { selectSelectionData } from '../search/searchSelectors';
import { addSeascapeLogActivity } from '../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../activity-logging/activityLogTypes';
import { showSuccessAlert } from '../../services/globalAlertService';
import { 
    trackEvent,
    trackExceptionWithContext
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';

function* addFilterSaga(action: PayloadAction<{ filter: IFilter }>) {
    const requestId = `add-filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.FILTER_CREATE_ATTEMPT,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterName: action.payload.filter.name,
                filterScope: action.payload.filter.scope,
                isDraft: action.payload.filter.isDraft
            }
        );

        const userId: number = yield select(selectUserId);
        const teamId: number | null = action.payload.filter.scope === ScopeOptions.Team ? yield select(selectTeamId) : null;
        const subscriptionId: number = yield select(selectSelectedSubscriptionId);
        const newFilterId: number = yield call(addFilterApi, action.payload.filter, teamId, subscriptionId);
        const newFilter: IFilter = { ...action.payload.filter, ...{ id: newFilterId, teamId: teamId, createdById: userId, isOwnFilter: true, isDraft: false } };
        yield put(addFilterSuccess({ filter: newFilter }));

        const payload = JSON.stringify({ FilterId: [newFilter.id], Name: newFilter.name });
        yield call(addSeascapeLogActivity, ({ eventId: SeascapeActivityLogConstants.FILTER_CREATE, payload }));

        trackEvent(
            AppInsightsActions.FILTER_CREATE_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: newFilter.id || 0,
                filterName: newFilter.name,
                filterScope: newFilter.scope
            }
        );

        showSuccessAlert(`Filter '${newFilter.name}' added successfully`);
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.FILTER_COMPONENT,
                action: AppInsightsActions.FILTER_CREATE_ATTEMPT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    filterName: action.payload.filter.name,
                    filterScope: action.payload.filter.scope
                }
            }
        );
        yield put(setFilterError({ message: error.message }));
    }
}

function* updateFilterSaga(action: PayloadAction<{ filter: IFilter }>) {
    const requestId = `update-filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.FILTER_UPDATE_ATTEMPT,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: action.payload.filter.id || 0,
                filterName: action.payload.filter.name,
                filterScope: action.payload.filter.scope
            }
        );

        const userId: number = yield select(selectUserId);
        const teamId: number | null = action.payload.filter.scope === ScopeOptions.Team ? yield select(selectTeamId) : null;
        yield call(updateFilterApi, action.payload.filter, teamId);
        const updatedFilter: IFilter = { ...action.payload.filter, ...{ teamId: teamId, createdById: action.payload.filter.isOwnFilter === true ? userId : null, isDraft: false } };
        yield put(updateFilterSuccess({ filter: updatedFilter }));
        
        trackEvent(
            AppInsightsActions.FILTER_UPDATE_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: updatedFilter.id || 0,
                filterName: updatedFilter.name
            }
        );

        showSuccessAlert(`Filter '${updatedFilter.name}' updated successfully`);
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.FILTER_COMPONENT,
                action: AppInsightsActions.FILTER_UPDATE_ATTEMPT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    filterId: action.payload.filter.id || 0,
                    filterName: action.payload.filter.name
                }
            }
        );
        yield put(setFilterError({ message: error.message }));
    }
}

function* deleteFilterSaga(action: PayloadAction<{ id: number }>) {
    const requestId = `delete-filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.FILTER_DELETE_ATTEMPT,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: action.payload.id
            }
        );

        yield call(deleteFilterApi, action.payload.id);
        yield put(deleteFilterSuccess({ deletedId: action.payload.id }));

        yield call(setSelectedVesselsAndOutputTypeAsDefaultSaga, action);

        trackEvent(
            AppInsightsActions.FILTER_DELETE_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: action.payload.id
            }
        );

        showSuccessAlert(`Filter deleted successfully`);
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.FILTER_COMPONENT,
                action: AppInsightsActions.FILTER_DELETE_ATTEMPT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    filterId: action.payload.id
                }
            }
        );
        yield put(setFilterError({ message: error.message }));
    }
}



function* getFiltersSaga() {
    const requestId = `get-filters-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.FILTER_DATA_FETCH_ATTEMPT,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT
            }
        );

        const teamId: number = yield select(selectTeamId);
        const userId: number = yield select(selectUserId);
        const subscriptionId: number = yield select(selectSelectedSubscriptionId);
        const filters: IFilter[] = yield call(getFiltersApi, teamId, subscriptionId);
        filters.forEach(filter => {
            filter.isOwnFilter = filter.createdById === userId;
            filter.isDraft = false
        });
        yield put(getFiltersSuccess({ filters }));

        trackEvent(
            AppInsightsActions.FILTER_DATA_FETCH_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filtersCount: filters.length,
                ownFiltersCount: filters.filter(f => f.isOwnFilter).length
            }
        );
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.FILTER_COMPONENT,
                action: AppInsightsActions.FILTER_DATA_FETCH_ATTEMPT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR
                }
            }
        );
        yield put(setFilterError({ message: error.message }));
    }
}

function* getFilterSaga(action: PayloadAction<{ id: number }>) {
    const requestId = `get-filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.FILTER_DATA_FETCH_ATTEMPT,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: action.payload.id
            }
        );

        const filter: IFilter = yield call(getFilterApi, action.payload.id);
        const userId: number = yield select(selectUserId);
        filter.isOwnFilter = filter.createdById === userId;
        filter.isDraft = false;
        yield put(getFilterSuccess({ filter }));

        trackEvent(
            AppInsightsActions.FILTER_DATA_FETCH_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: filter.id || 0,
                filterName: filter.name,
                isOwnFilter: filter.isOwnFilter
            }
        );

        // Update dashboard to default if selection is refreshed
        yield call(setSelectedVesselsAndOutputTypeAsDefaultSaga, action);

    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.FILTER_COMPONENT,
                action: AppInsightsActions.FILTER_DATA_FETCH_ATTEMPT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    filterId: action.payload.id
                }
            }
        );
        yield put(setFilterError({ message: error.message }));
    }
}

function* displayOutputByFilterIdSaga(action: PayloadAction<{ id: number | string }>) {
    const requestId = `apply-filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.FILTER_APPLY_ATTEMPT,
            {
                requestId,
                component: AppInsightsComponents.FILTER_COMPONENT,
                filterId: typeof action.payload.id === 'number' ? action.payload.id : 0
            }
        );

        const filter: IFilter = yield select((state: RootState) => state.filterSlice.filters.find(p => p.id === action.payload.id || p.tempId === action.payload.id));
        const filterWrapper = new FilterWrapper(filter.ownershipFilter ?? '[]', filter.sectorFilter ?? '[]');
        const tempSubscribedMsiVessels: TSubscribedMsiVessel[] = yield select(selectSubscribedMsiVessels); // read all to apply filter
        const vesselList: IVesselFormData[] = FilterWrapper.applyFilters(tempSubscribedMsiVessels, filterWrapper.ownershipFilter.ownerships, filterWrapper.sectorFilter.sectors);

        yield put(displayOutputByFilterSuccess());
        if (vesselList.length === 0) {
            trackEvent(
                AppInsightsActions.FILTER_APPLY_FAILURE,
                {
                    requestId,
                    component: AppInsightsComponents.FILTER_COMPONENT,
                    filterId: filter.id || 0,
                    filterName: filter.name,
                    reason: "No vessels found"
                }
            );

            yield call(() => ModalManager.show({
                header: "Warning",
                message: "No vessels found in Filter. Please change your filter or contact MSI.",
                showActionButton: false, size: "sm"
            }));
        }
        else {            
            trackEvent(
                AppInsightsActions.FILTER_APPLY_SUCCESS,
                {
                    requestId,
                    component: AppInsightsComponents.FILTER_COMPONENT,
                    filterId: filter.id || 0,
                    filterName: filter.name,
                    vesselsFound: vesselList.length,
                    outputType: vesselList.length === 1 ? "Vessel" : vesselList.length > 200 ? "Summary" : "Fleet"
                }
            );

            yield put(setSelectedVesselsForOutputRequest({
                outputSelection: {
                    selectedVesselsList: vesselList,
                    selectionName: filter.name, selectionType: filter.isDraft ? ESelectionType.DraftFilter : ESelectionType.Filter,
                    outputType: vesselList.length === 1 ? EOutputType.Vessel : vesselList.length > 200 ? EOutputType.Summary : EOutputType.Fleet,
                    selectionTypeObject: {
                        id: filter.id, name: filter.name,
                        scope: filter.scope, isOwnFilter: filter.isOwnFilter,
                        isDraft: filter.isDraft, tempId: filter.tempId
                    } as IFilterFormData
                }
            }))
        }

    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.FILTER_COMPONENT,
                action: AppInsightsActions.FILTER_APPLY_ATTEMPT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.FILTER_APPLICATION_ERROR,
                    filterId: typeof action.payload.id === 'number' ? action.payload.id : 0
                }
            }
        );
        yield put(setFilterError({ message: error.message }));
    }
}

function* setSelectedVesselsAndOutputTypeAsDefaultSaga(action: PayloadAction<{ id?: number | null, tempId?: string }>) {
    try {
        const selection: ISelectedVesselsFormData = yield select(selectSelectionData);
        if ((selection.selectionTypeObject as IFilterFormData)?.id === action.payload.id ||
            ((selection.selectionTypeObject as IFilterFormData)?.tempId && (selection.selectionTypeObject as IFilterFormData)?.tempId === action.payload.tempId)) {
            const vessels: IVesselFormData[] = yield select(selectSubscribedMsiVessels);
            const outputType: EOutputType = vessels.length === 1 ? EOutputType.Vessel : vessels.length > 200 ? EOutputType.Summary : EOutputType.Fleet;

            const outputSelection: ISelectedVesselsFormData = {
                selectedVesselsList: vessels,
                outputType: outputType,
                selectionType: ESelectionType.None,
                selectionName: '',
            };

            yield put(setSelectedVesselsForOutputRequest({ outputSelection }));
        }
    } catch (error: any) {
        //TODO: discuss how to manage this type of error @Muhammed @Evrim
        ModalManager.show({ header: 'Error', message: JSON.stringify(error) });
    }
}
export default function* watchFilterActions(): Generator {
    yield takeLatest(actionTypes.ADD_FILTER_REQUEST_ACTION, addFilterSaga);
    yield takeLatest(actionTypes.UPDATE_FILTER_REQUEST_ACTION, updateFilterSaga);
    yield takeLatest(actionTypes.GET_FILTERS_REQUEST_ACTION, getFiltersSaga);
    yield takeLatest(actionTypes.GET_FILTER_REQUEST_ACTION, getFilterSaga);
    yield takeLatest(actionTypes.DELETE_FILTER_REQUEST_ACTION, deleteFilterSaga);
    // yield takeLatest(actionTypes.SET_SELECTED_FILTER_REQUEST_ACTION, setSelectedFilterSaga);
    yield takeLatest(actionTypes.DISPLAY_OUTPUT_BY_FILTER_ID_REQUEST_ACTION, displayOutputByFilterIdSaga);
    yield takeLatest(actionTypes.DELETE_DRAFT_FILTER_REQUEST_ACTION, setSelectedVesselsAndOutputTypeAsDefaultSaga);
    // yield takeLatest(actionTypes.DISPLAY_OUTPUT_BY_FILTER_IMO_LIST_REQUEST_ACTION, displayOutputByFilterImoListSaga);
}

